import React from 'react';
import { useHistory } from 'react-router';
import { useFormik } from 'formik';
import { Button, FormControlLabel, Switch, TextField, Typography } from '@material-ui/core';
import SelectField from 'components/SelectField';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from 'components/ConfirmDialog';
import UserFormProps from 'types/forms/UserFormProps';
import User from 'types/models/User';
import { storeUser, updateUser } from 'services/userService';
import { UserFormCreate, UserFormEdit } from './schemas/UserForm';
import ProfileTypes from 'types/enums/ProfileTypes';
import Venue from 'types/models/Venue';

export default ({ venues, user }: UserFormProps): JSX.Element => {
  const history = useHistory();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [error, setError] = React.useState('');
  const { t } = useTranslation();

  const venuesForSelect = (venues: Venue[]) => {
    return venues.map((venue) => {
      return {
        label: venue.code,
        value: venue.id,
      };
    });
  };

  const onCancel = (): void => {
    formik.setSubmitting(false);
  };

  const onConfirm = async (): Promise<void> => {
    const userData = {
      ...formik.values,
    };

    try {
      let result: User | null;

      if (user) {
        result = await updateUser(user.id, userData);
      } else {
        result = await storeUser(userData);
      }

      if (result) {
        history.go(-1);
      }
    } catch (error: any) {
      console.log(error);
      setError(`${error}`);
      formik.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: user?.email ?? '',
      password: '',
      fullname: user?.fullname ?? '',
      venueIds: user?.venues?.map(({ id }) => id) ?? [],
      profileTypeId: ProfileTypes.VenueEditor,
      showUserSection: user?.showUserSection ?? false,
      showSplashSection: user?.showSplashSection ?? false,
      showUserSplashSection: user?.showUserSplashSection ?? false,
    },
    validationSchema: user ? UserFormEdit(t) : UserFormCreate(t),
    onSubmit: () => {
      setOpenDialog(true);
    },
  });

  return (
    <>
      <ConfirmDialog open={openDialog} setOpen={setOpenDialog} onConfirm={onConfirm} onCancel={onCancel} />
      <form onSubmit={formik.handleSubmit}>
        <SelectField
          name="venueIds"
          label={t('select-venue')}
          placeholder={t('select-venue')}
          value={formik.values.venueIds}
          options={venuesForSelect(venues)}
          isMulti={true}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.venueIds}
          error={formik.errors.venueIds}
          isClearable={true}
          backspaceRemovesValue={true}
          isDisabled={formik.isSubmitting}
          isSearchable={true}
        />
        <TextField
          margin="dense"
          fullWidth
          id="email"
          name="email"
          label={t('upper-email')}
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          disabled={formik.isSubmitting}
        />
        <TextField
          margin="dense"
          fullWidth
          id="password"
          name="password"
          type="password"
          label={t('upper-password')}
          value={formik.values.password}
          onChange={formik.handleChange}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          disabled={formik.isSubmitting}
        />
        <FormControlLabel
          id="showUserSection"
          name="showUserSection"
          control={<Switch checked={formik.values.showUserSection} onChange={formik.handleChange} />}
          label={t('can-see-users-section')}
          disabled={formik.isSubmitting}
        />
        <FormControlLabel
          id="showSplashSection"
          name="showSplashSection"
          control={<Switch checked={formik.values.showSplashSection} onChange={formik.handleChange} />}
          label={t('can-see-splashes-section')}
          disabled={formik.isSubmitting}
        />
        <FormControlLabel
          id="showUserSplashSection"
          name="showUserSplashSection"
          control={<Switch checked={formik.values.showUserSplashSection} onChange={formik.handleChange} />}
          label={t('can-see-both-sections')}
          disabled={formik.isSubmitting}
        />

        <TextField
          margin="dense"
          fullWidth
          id="fullname"
          name="fullname"
          label={t('username')}
          value={formik.values.fullname}
          onChange={formik.handleChange}
          error={formik.touched.fullname && Boolean(formik.errors.fullname)}
          helperText={formik.touched.fullname && formik.errors.fullname}
          disabled={formik.isSubmitting}
        />
        <div className="footer">
          <Button fullWidth variant="contained" color="primary" type="submit" disabled={formik.isSubmitting}>
            {`${user ? t('edit') : t('create')}`}
          </Button>
        </div>
        {error && (
          <div>
            <Typography color="error" variant="h6" component="h2">
              {error}
            </Typography>
          </div>
        )}
      </form>
    </>
  );
};
